import { useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { loginDestination } from '@/lib/auth-navigation'
import { useAuth } from '@/contexts/AuthContext'
import { useForm } from 'react-hook-form'
import { authApi } from '@/lib/api'
import { Building2, ChevronLeft, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'

interface CredentialsForm {
  email: string
  password: string
}

interface TenantOption {
  slug: string
  name: string
}

export default function LoginPage() {
  const { login, isAuthenticated } = useAuth()
  const location = useLocation()

  const [step, setStep] = useState<'credentials' | 'selecting'>('credentials')
  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [pendingCreds, setPendingCreds] = useState<CredentialsForm | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [selectingTenant, setSelectingTenant] = useState<string | null>(null)

  const { register, handleSubmit, formState: { errors } } = useForm<CredentialsForm>({
    defaultValues: { email: '', password: '' },
  })

  if (isAuthenticated) {
    return <Navigate to={loginDestination(location.search)} replace />
  }

  // Step 1: validate credentials and discover tenants
  const onSubmitCredentials = async (data: CredentialsForm) => {
    try {
      setIsLoading(true)
      const res = await authApi.userTenants({ email: data.email, password: data.password })
      const available = res.data.tenants

      if (available.length === 0) {
        toast.error('Email ou password incorretos')
        return
      }

      if (available.length === 1) {
        // Single tenant — log in directly
        await login({ email: data.email, password: data.password, tenant_slug: available[0].slug })
        return
      }

      // Multiple tenants — show selector
      setPendingCreds(data)
      setTenants(available)
      setStep('selecting')
    } catch {
      toast.error('Email ou password incorretos')
    } finally {
      setIsLoading(false)
    }
  }

  // Step 2: user picks a tenant
  const onSelectTenant = async (slug: string) => {
    if (!pendingCreds) return
    try {
      setSelectingTenant(slug)
      await login({ email: pendingCreds.email, password: pendingCreds.password, tenant_slug: slug })
    } catch {
      toast.error('Erro ao entrar na empresa')
    } finally {
      setSelectingTenant(null)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-primary-600">Shopfloor Hub</h1>
          <p className="mt-2 text-gray-600">Portal de Administração</p>
        </div>

        <div className="mt-8 rounded-lg border bg-white p-8 shadow-sm">
          {step === 'credentials' ? (
            <>
              <h2 className="text-2xl font-semibold">Iniciar Sessão</h2>
              <p className="mt-1 text-sm text-gray-600">Entre com as suas credenciais</p>

              <form onSubmit={handleSubmit(onSubmitCredentials)} className="mt-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    {...register('email', {
                      required: 'Email obrigatório',
                      pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Email inválido' },
                    })}
                    type="email"
                    className="input mt-1"
                    placeholder="email@exemplo.com"
                    autoComplete="email"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    {...register('password', { required: 'Password obrigatória' })}
                    type="password"
                    className="input mt-1"
                    placeholder="••••••••"
                    autoComplete="current-password"
                  />
                  {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>}
                </div>

                <button type="submit" disabled={isLoading} className="btn btn-primary w-full">
                  {isLoading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      A verificar...
                    </span>
                  ) : (
                    'Continuar'
                  )}
                </button>
              </form>

              <div className="mt-6 rounded-lg bg-blue-50 p-4">
                <p className="text-sm font-medium text-blue-900">Solicitar credenciais de acesso:</p>
                <p className="mt-1 text-sm text-blue-700">Email: carlos.joao@aquinos.pt</p>
                <p className="text-sm text-blue-700">WhatsApp: +351 968 776 646</p>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep('credentials')}
                className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
              >
                <ChevronLeft className="h-4 w-4" />
                Voltar
              </button>

              <h2 className="text-xl font-semibold text-gray-900">Escolher Empresa</h2>
              <p className="mt-1 text-sm text-gray-500">
                A sua conta tem acesso a {tenants.length} empresas. Selecione onde quer entrar:
              </p>

              <div className="mt-5 space-y-2">
                {tenants.map((t) => (
                  <button
                    key={t.slug}
                    onClick={() => onSelectTenant(t.slug)}
                    disabled={!!selectingTenant}
                    className="flex w-full items-center gap-3 rounded-lg border border-gray-200 p-4 text-left transition hover:border-primary-400 hover:bg-primary-50 disabled:opacity-60"
                  >
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                      {selectingTenant === t.slug ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <Building2 className="h-5 w-5" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t.name}</p>
                      <p className="text-xs text-gray-400">{t.slug}</p>
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
